import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { NotasContext } from "../../contexts/NotasContext";
import { enviarNotaAAparte } from "../../lib/notasAparte";
import { nt, puedeNotas, myId, errorNota, fijarUsuarioPrecarga, fijarUsuarioUltimoIntento } from "../../lib/notas";
import { toast } from "sonner";
import NotasFlotantes from "./NotasFlotantes";
import PopupCuentas from "./PopupCuentas";
import "./notas.css";

const MAX_FLOTANTES = 6;

/** Estado global del módulo Notas: notas flotantes (sobre cualquier módulo) y la ventanita «¿por dónde llamar?». */
export default function NotasProvider({ children }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const habilitado = puedeNotas(user);
  const uid = myId(user);
  const enAparte = location.pathname.startsWith("/notas-aparte");
  const [flotantes, setFlotantes] = useState([]);
  const [popup, setPopup] = useState(null);

  // Otra persona en este navegador: nada de lo anterior se arrastra.
  useEffect(() => { setFlotantes([]); setPopup(null); fijarUsuarioPrecarga(uid); fijarUsuarioUltimoIntento(uid); }, [uid]);   // y la entrega «nota -> teléfono»/último intento de otra persona se borra

  const abrirFlotante = useCallback((id, titulo, color) => {
    setFlotantes((lista) => {
      const sig = Math.max(0, ...lista.map((f) => f.z)) + 1;
      const ya = lista.find((f) => f.id === id);
      if (ya) return lista.map((f) => (f.id === id ? { ...f, min: false, z: sig } : f));
      if (lista.length >= MAX_FLOTANTES) { toast.error("Hay demasiadas notas abiertas. Cierra alguna primero."); return lista; }
      const n = lista.length;
      return [...lista, { id, titulo: titulo || "", color: color || "#FFD84D", x: 90 + n * 36, y: 90 + n * 36, w: 340, h: 340, min: false, z: sig }];
    });
  }, []);
  const cerrarFlotante = useCallback((id) => setFlotantes((l) => l.filter((f) => f.id !== id)), []);
  const minimizarFlotante = useCallback((id) => setFlotantes((l) => { const sig = Math.max(0, ...l.map((f) => f.z)) + 1; return l.map((f) => (f.id === id ? { ...f, min: !f.min, z: f.min ? sig : f.z } : f)); }), []);
  const moverFlotante = useCallback((id, x, y) => setFlotantes((l) => l.map((f) => (f.id === id ? { ...f, x, y } : f))), []);
  const traerAlFrente = useCallback((id) => setFlotantes((l) => { const top = Math.max(0, ...l.map((f) => f.z)); const f = l.find((x) => x.id === id); if (!f || f.z === top) return l; return l.map((x) => (x.id === id ? { ...x, z: top + 1 } : x)); }), []);
  const fijarTitulo = useCallback((id, titulo, color) => setFlotantes((l) => l.map((f) => (f.id === id && (f.titulo !== titulo || f.color !== color) ? { ...f, titulo, color: color || f.color } : f))), []);
  const pedirLlamada = useCallback((phone, noteId) => { if (phone) setPopup({ phone, noteId: noteId || null }); }, []);
  const enviarAAparte = useCallback((id) => { enviarNotaAAparte(id); cerrarFlotante(id); }, [cerrarFlotante]);

  const nuevaFlotante = useCallback(async () => {
    try { const r = await nt.post("", {}); abrirFlotante(r.data.id, r.data.title, r.data.color); }
    catch (e) { toast.error(errorNota(e)); }
  }, [abrirFlotante]);

  const value = useMemo(() => ({
    flotantes, abrirFlotante, cerrarFlotante, minimizarFlotante, moverFlotante, traerAlFrente, pedirLlamada, enviarAAparte,
  }), [flotantes, abrirFlotante, cerrarFlotante, minimizarFlotante, moverFlotante, traerAlFrente, pedirLlamada, enviarAAparte]);

  return (
    <NotasContext.Provider value={value}>
      {children}
      {habilitado && !enAparte && (
        <NotasFlotantes flotantes={flotantes} onMover={moverFlotante} onFrente={traerAlFrente} onMin={minimizarFlotante} onCerrar={cerrarFlotante}
          onAparte={enviarAAparte} onTitulo={fijarTitulo} onNueva={nuevaFlotante} onTodas={() => navigate("/notas")} />
      )}
      {habilitado && popup && (
        <div className="nt"><PopupCuentas phone={popup.phone} noteId={popup.noteId} onCerrar={() => setPopup(null)} /></div>
      )}
    </NotasContext.Provider>
  );
}
